import { useOktaAuth } from "@okta/okta-react";
import { createFileRoute, Link, Outlet, useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { AxleLogoColor, MdRoundArrowBack, MdRoundLogout } from "@teamworksdev/icons";
import { VerticalNav, VerticalNavItem, VerticalNavLink } from "@teamworksdev/nav";
import { Avatar, Badge, Button, IconButton } from "@teamworksdev/react";
import { OrgPicker2, WithTooltip } from "~/components";
import { isGovCloud } from "~/config";
import { getAxleDataFromOrgDocument, useRawOrgDocumentQuery } from "~/data/orgs";
import { useDuplicateProfilesExistenceQuery } from "~/modules/Duplicates/queries/useDuplicateProfilesExistenceQuery";
import { usePartialMatchCountsQuery } from "~/modules/PartialMatches/queries";
import { useFeatureFlag } from "~/services/launchDarkly";

export const Route = createFileRoute("/_protected/(org)/_org-layout")({
  component: OrgLayout,
});

const ORG_ROOT_ROUTE = "/orgs/$orgId";
const isSupportToolsEnabled = import.meta.env.VITE_SUPPORT_TOOLS_ENABLED === "true";

function OrgLayout() {
  const { orgId } = useParams({ strict: false });
  const orgPath = ORG_ROOT_ROUTE.replace("$orgId", orgId ?? "");
  const duplicatesEnabled = useFeatureFlag("potential-duplicates-enabled", false);
  const navigate = useNavigate();
  const location = useLocation();

  const { oktaAuth } = useOktaAuth();

  const { data: partialMatchCounts } = usePartialMatchCountsQuery({
    params: { by: "org" },
  });

  const { data: hasDuplicates } = useDuplicateProfilesExistenceQuery({
    orgId,
    enabled: duplicatesEnabled,
  });

  const { data: selectedOrgDocument } = useRawOrgDocumentQuery({
    documentId: orgId,
  });

  const axleData = getAxleDataFromOrgDocument(selectedOrgDocument);

  return (
    <div className={`flex grow bg-light-25 ${isGovCloud ? "h-[calc(100vh_-_40px)]" : "h-screen"}`}>
      <div
        className={`flex flex-col w-[260px] shrink-0 ${isGovCloud ? "h-[calc(100vh_-_40px)]" : "h-screen"} border-r border-light-200`}
      >
        <div className="h-[52px] flex items-center justify-between bg-[#343434] px-4">
          <Link to="/">
            <Button className="text-white hover:text-white hover:bg-black/20 gap-2" size="sm" variant="ghost">
              <AxleLogoColor width={16} height={16} />
              Axle
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <WithTooltip title="Back to organizations" asChild>
              <IconButton
                aria-label="Back"
                className="px-1.5 hover:bg-black/20"
                icon={<MdRoundArrowBack color="white" size={16} />}
                variant="ghost"
                onClick={() => navigate({ to: "/" })}
              />
            </WithTooltip>
            <WithTooltip title="Logout" asChild>
              <IconButton
                aria-label="Logout"
                className="px-1.5 hover:bg-black/20"
                icon={<MdRoundLogout color="white" size={16} />}
                variant="ghost"
                onClick={() => oktaAuth.signOut()}
              />
            </WithTooltip>
          </div>
        </div>
        <div className="flex flex-col grow p-4 bg-white">
          <OrgPicker2
            onChange={(orgId) => {
              // Preserve the current path, but replace the orgId
              const pathnameParts = location.pathname.split("/").filter(Boolean); // Filter out empty strings from leading slashes

              // Strip the basepath and replace the actual orgId with the param signifier
              const orgsPartIndex = pathnameParts.indexOf("orgs");
              const newPathnameParts = pathnameParts.slice(orgsPartIndex);
              newPathnameParts.splice(1, 1, "$orgId");

              const newTo = `/${newPathnameParts.join("/")}`;

              navigate({
                to: newTo,
                params: { orgId },
              });
            }}
            selectedOrgId={axleData?.orgId}
          />
          <VerticalNav className="mt-3">
            <VerticalNavItem>
              <VerticalNavLink to={orgPath}>
                <div className="flex items-center gap-2 py-1.5">
                  <Avatar
                    className="shrink-0"
                    name={axleData?.name ?? ""}
                    size={20}
                    src={axleData?.navbarImageUrl ?? undefined}
                  />
                  <span className="text-sm font-semibold break-words text-black/high-emphasis">{axleData?.name}</span>
                </div>
              </VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/app-data`}>App data</VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/teams`}>Teams</VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/app-access-rules`}>App access rules</VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/people`}>People</VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/team-mapping`}>Team mapping (legacy)</VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/user-types`}>User types</VerticalNavLink>
            </VerticalNavItem>
            <VerticalNavItem>
              <VerticalNavLink to={`${orgPath}/partial-matches`}>
                <span>Partial matches</span>
                <Badge className="ml-auto" value={partialMatchCounts?.[orgId ?? ""] ?? 0} colorScheme="count" />
              </VerticalNavLink>
            </VerticalNavItem>
            {duplicatesEnabled && (
              <VerticalNavItem>
                <VerticalNavLink to={`${orgPath}/duplicates`}>
                  <span>Potential duplicates</span>
                  {hasDuplicates && (
                    <span
                      role="img"
                      aria-label="Potential duplicates exist"
                      className="ml-auto w-2 h-2 rounded-full bg-info-700"
                    />
                  )}
                </VerticalNavLink>
              </VerticalNavItem>
            )}
            {isSupportToolsEnabled && (
              <VerticalNavItem>
                <VerticalNavLink to={`${orgPath}/support-tools`}>Support tools</VerticalNavLink>
              </VerticalNavItem>
            )}
          </VerticalNav>
        </div>
      </div>
      <div className={`grow overflow-y-auto ${isGovCloud ? "pb-8" : ""}`}>
        <Outlet />
      </div>
    </div>
  );
}
